import { useMutation, useQuery } from '@tanstack/react-query';
import type { JSX } from 'react';

import { ConfirmDialog } from '@/components/ConfirmDialog';

import {
  getTenantStatusImpact,
  updateTenantStatus,
  type TenantLifecycleOption,
  type TenantStatusResult,
} from '../api/tenants.graphql';
import { LIFECYCLE_ACTIONS, STATUS_REASON_LIMITS, statusLabel } from '../tenant-labels';

interface Props {
  tenant: { id: string; name: string };
  /** Acción que ofreció el API (`allowedLifecycleActions`): destino y si exige motivo. */
  option: TenantLifecycleOption;
  onDone: (result: TenantStatusResult) => void;
  onCancel: () => void;
}

function plural(count: number, one: string, many: string): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * OS-1713 / OS-1720 — confirmación de una acción del ciclo de vida. Las que cortan el acceso
 * muestran el impacto; el motivo es obligatorio cuando el API lo exige y opcional en el resto (si
 * se escribe, queda en la auditoría). El error real de la mutación se muestra en el diálogo.
 */
export function TenantStatusDialog({ tenant, option, onDone, onCancel }: Props): JSX.Element {
  const view = LIFECYCLE_ACTIONS[option.action];
  const impact = useQuery({
    queryKey: ['platform-tenant-status-impact', tenant.id],
    queryFn: () => getTenantStatusImpact(tenant.id),
    enabled: view.showImpact,
    staleTime: 0,
  });
  const mutation = useMutation({
    mutationFn: (reason: string) => updateTenantStatus(tenant.id, option.to, reason),
    onSuccess: onDone,
  });

  return (
    <ConfirmDialog
      title={`${view.title}: ${tenant.name}`}
      confirmLabel={view.confirmLabel}
      cancelLabel="Volver"
      danger={view.danger}
      pending={mutation.isPending}
      error={mutation.error?.message ?? null}
      reason={{
        label: 'Motivo (queda en la auditoría)',
        placeholder: view.reasonPlaceholder,
        optional: !option.reasonRequired,
        ...STATUS_REASON_LIMITS,
      }}
      onConfirm={(reason) => mutation.mutate(reason)}
      onCancel={onCancel}
    >
      <p>{view.description}</p>
      {option.action === 'RESUME' || option.action === 'REACTIVATE' ? (
        <p>Queda en estado: {statusLabel(option.to).toLowerCase()}.</p>
      ) : null}
      {view.showImpact ? (
        <p aria-live="polite">
          {impact.isLoading
            ? 'Calculando el impacto…'
            : impact.data
              ? `Perderán el acceso ${plural(impact.data.activeUsers, 'usuario activo', 'usuarios activos')} y ${plural(impact.data.activeOperationalUsers, 'usuario operativo (mesero, cocina, planta)', 'usuarios operativos (meseros, cocina, planta)')}.`
              : 'No se pudo calcular el impacto.'}
        </p>
      ) : null}
    </ConfirmDialog>
  );
}
